import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  MdAdd,
  MdDelete,
  MdOpenInNew,
} from "react-icons/md";
import { FiLayers, FiDatabase } from "react-icons/fi";
import TableNavTabs from "./TableNavTabs";
import DynamicTable from "../../Components/Table/DynamicTable";
import Loader from "../../Components/Loader/Loader";
import {
  fetchModules,
  addModule,
  updateModule,
  fetchModuleDetails,
  addTableInModule,
  clearStatusMessage,
} from "../../Redux/Slice/TableManagementSlice";

const ModuleMaster = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    modules,
    modulesLoading,
    moduleDetails,
    moduleDetailsLoading,
    statusMessage,
    actionLoading,
    actionError,
  } = useSelector((state) => state.TableManagement);

  const [moduleName, setModuleName] = useState("");
  const [updateName, setUpdateName] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  // Initial load
  useEffect(() => {
    dispatch(fetchModules());
  }, [dispatch]);

  // Handle toast notifications
  useEffect(() => {
    if (statusMessage) {
      toast.success(statusMessage);
      dispatch(clearStatusMessage());
    }
    if (actionError) {
      toast.error(actionError);
      dispatch(clearStatusMessage());
    }
  }, [statusMessage, actionError, dispatch]);

  // Sync update input when row selection changes
  useEffect(() => {
    if (selectedRow) {
      setUpdateName(selectedRow.ModuleName || "");
    } else {
      setUpdateName("");
    }
  }, [selectedRow]);

  // Main modules grid rows
  const moduleRows = useMemo(() => {
    const list = modules?.data || [];
    return list.map((item, index) => ({
      id: index + 1,
      ModuleName: item?.module_name,
      TableName: item?.detail_id?.length || 0,
      Mid: item?._id,
    }));
  }, [modules]);

  // Tables currently assigned to this module
  const assignedTablesRows = useMemo(() => {
    const tableList = moduleDetails?.All_Type?.detail_id || [];
    return tableList.map((item, index) => ({
      id: index + 1,
      TableName: item?.id?.tblName || "Unnamed Table",
      Mid: item?.id?._id || item?._id,
    }));
  }, [moduleDetails]);

  // Available tables that can be added
  const availableTablesRows = useMemo(() => {
    const filterList = moduleDetails?.Fillter_Type || [];
    return filterList.map((item, index) => ({
      id: index + 1,
      TableName: item?.tblName || "Unnamed Table",
      Mid: item?._id,
    }));
  }, [moduleDetails]);

  // Row selection handler
  const handleRowClick = (row) => {
    const activeRow = row?.row || row;
    setSelectedRow(activeRow);
    if (activeRow?.Mid) {
      dispatch(fetchModuleDetails(activeRow.Mid));
    }
  };

  // Add Module
  const handleAddSubmit = (e) => {
    e?.preventDefault();
    if (!moduleName.trim()) {
      toast.warn("Please enter a module name");
      return;
    }
    dispatch(addModule(moduleName.trim()));
    setModuleName("");
  };

  // Update Module Name
  const handleUpdateSubmit = (e) => {
    e?.preventDefault();
    if (!updateName.trim()) {
      toast.warn("Please enter updated module name");
      return;
    }
    if (!selectedRow?.Mid) return;

    dispatch(updateModule({ id: selectedRow.Mid, moduleName: updateName.trim() }));
    setSelectedRow((prev) => (prev ? { ...prev, ModuleName: updateName.trim() } : null));
  };

  // Add / Remove Table from Module
  const handleToggleTable = (tableId) => {
    if (!selectedRow?.Mid || !tableId) return;
    dispatch(
      addTableInModule({
        moduleId: selectedRow.Mid,
        tableId,
      })
    );
  };

  // Main columns for DynamicTable
  const moduleColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "ModuleName",
      headerName: "Module Name",
      flex: 2,
      renderCell: (params) => (
        <span className="font-semibold text-gray-800 flex items-center gap-2">
          <FiLayers className="text-[#133a69]" />
          {params.row.ModuleName}
        </span>
      ),
    },
    {
      field: "TableName",
      headerName: "Total Tables",
      flex: 1,
      renderCell: (params) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-[#E8F2FF] text-[#133a69]">
          {params.row.TableName} Tables
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isSelected = selectedRow?.Mid === params.row.Mid;
        return (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => handleRowClick(params.row)}
              className={`px-3 py-1 text-xs font-semibold rounded transition ${
                isSelected
                  ? "bg-[#133a69] text-white"
                  : "bg-gray-200 hover:bg-[#133a69] hover:text-white text-gray-800"
              }`}
            >
              {isSelected ? "Selected" : "Select"}
            </button>
            <button
              onClick={() => navigate("/TableMaster")}
              title="Jump to Table Master"
              className="text-gray-500 hover:text-[#133a69] p-1"
            >
              <MdOpenInNew size={16} />
            </button>
          </div>
        );
      },
    },
  ];

  // Assigned tables columns for DynamicTable
  const assignedTablesColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "actions",
      headerName: "Remove",
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleToggleTable(params.row.Mid)}
            className="bg-red-600 text-white p-1 rounded hover:bg-red-700 transition"
            title="Remove from module"
          >
            <MdDelete size={16} />
          </button>
        </div>
      ),
    },
    {
      field: "TableName",
      headerName: "Table Name",
      flex: 2,
      renderCell: (params) => (
        <span className="flex items-center gap-2 text-gray-800 font-medium">
          <FiDatabase className="text-red-500" />
          {params.row.TableName}
        </span>
      ),
    },
  ];

  // Available tables columns for DynamicTable
  const availableTablesColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "actions",
      headerName: "Add",
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleToggleTable(params.row.Mid)}
            className="All_button text-white p-1 rounded hover:opacity-90 transition"
            title="Add to module"
          >
            <MdAdd size={16} />
          </button>
        </div>
      ),
    },
    {
      field: "TableName",
      headerName: "Table Name",
      flex: 2,
      renderCell: (params) => (
        <span className="flex items-center gap-2 text-gray-800 font-medium">
          <FiDatabase className="text-green-600" />
          {params.row.TableName}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Page Title & Navigation Tabs */}
      <TableNavTabs title="Module Master" />

      {/* Main Grid: Form Left, Modules Table Right */}
      <div className="grid grid-cols-12 gap-4">
        {/* Form Section */}
        <div className="col-span-12 lg:col-span-5">
          <div className="Form_bg_color bg-white shadow-md rounded-md p-4">
            <div className="mb-4">
              <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                Module Details
              </h3>
            </div>

            {/* Create Module Form */}
            <form onSubmit={handleAddSubmit} className="space-y-3">
              <div>
                <label className="font-semibold block mb-1 text-sm text-gray-700">
                  Module Name <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter Module Name"
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded text-sm outline-none focus:border-[#133a69]"
                  />
                  <button
                    type="submit"
                    disabled={actionLoading || !moduleName.trim()}
                    className="All_button text-white px-6 py-2 rounded transition hover:opacity-90 font-medium text-sm whitespace-nowrap"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>

            {/* Update Module Section */}
            {selectedRow ? (
              <div className="mt-4 pt-4 border-t border-gray-200 animate__animated animate__fadeIn">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-xs text-[#133a69] uppercase tracking-wider">
                    Update Selected Module
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedRow(null)}
                    className="text-xs text-red-600 hover:underline font-semibold"
                  >
                    Clear Selection
                  </button>
                </div>
                <div className="text-xs text-gray-600 mb-2">
                  Selected: <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{selectedRow.ModuleName}</span>
                </div>
                <div>
                  <label className="font-semibold block mb-1 text-sm text-gray-700">
                    Update Module Name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter Updated Module Name"
                      value={updateName}
                      onChange={(e) => setUpdateName(e.target.value)}
                      className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded text-sm outline-none focus:border-[#133a69]"
                    />
                    <button
                      type="button"
                      onClick={handleUpdateSubmit}
                      disabled={actionLoading || !updateName.trim()}
                      className="All_button text-white px-6 py-2 rounded transition hover:opacity-90 font-medium text-sm whitespace-nowrap"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-gray-50 border border-dashed border-gray-300 rounded text-xs text-gray-500">
                Click any module row in the table to edit its name or manage assigned tables.
              </div>
            )}
          </div>
        </div>

        {/* Modules Table Section */}
        <div className="col-span-12 lg:col-span-7">
          <div className="Form_bg_color bg-white shadow-md rounded-md p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                All Modules
              </h3>
              <span className="bg-[#112A47] text-white px-4 py-2 text-sm font-bold rounded shadow">
                Total: {moduleRows.length}
              </span>
            </div>
            {modulesLoading ? (
              <div className="p-8 flex justify-center">
                <Loader />
              </div>
            ) : (
              <DynamicTable
                columns={moduleColumns}
                rows={moduleRows}
                height={380}
                searchable={true}
                onRowClick={handleRowClick}
                getRowClassName={(row) =>
                  row.Mid === selectedRow?.Mid ? "!bg-blue-100 font-bold" : ""
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* Detail Section: Shows Assigned & Available Tables when a Module is selected */}
      {selectedRow ? (
        <div className="Form_bg_color bg-white shadow-md rounded-md p-4 mt-4 animate__animated animate__fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-3 mb-4">
            <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
              Tables in Module: {selectedRow.ModuleName}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/TableMaster")}
                className="All_button text-white px-4 py-2 rounded text-xs font-semibold hover:opacity-90 transition flex items-center gap-1 shadow"
              >
                Jump to Table Master <MdOpenInNew size={14} />
              </button>
              <button
                onClick={() => setSelectedRow(null)}
                className="bg-gray-400 text-white px-4 py-2 rounded text-xs font-semibold hover:bg-gray-500 transition shadow"
              >
                Close
              </button>
            </div>
          </div>

          {moduleDetailsLoading ? (
            <div className="p-8 flex justify-center">
              <Loader />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-sm mb-2 text-gray-700">
                  Tables In Module ({assignedTablesRows.length})
                </h4>
                <DynamicTable
                  columns={assignedTablesColumns}
                  rows={assignedTablesRows}
                  height={300}
                  searchable={true}
                />
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-2 text-gray-700">
                  Available Tables to Add ({availableTablesRows.length})
                </h4>
                <DynamicTable
                  columns={availableTablesColumns}
                  rows={availableTablesRows}
                  height={300}
                  searchable={true}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ModuleMaster;
